import { useState, useEffect, useCallback, useRef } from 'react'
import { getSignedUrl } from '../lib/upload'
import { cn } from '../lib/utils'

// ── Offline placeholder ────────────────────────────────────────
const OfflinePlaceholder = () => (
  <div className="flex flex-col items-center gap-3 text-gray-400">
    <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/>
      <line x1="4" y1="4" x2="20" y2="20" strokeWidth={2}/>
    </svg>
    <p className="text-sm">Available when online</p>
  </div>
)

// ── Caption editor ────────────────────────────────────────────
const CaptionEditor = ({ caption, onSave, onCancel }) => {
  const [value, setValue] = useState(caption ?? '')
  return (
    <div className="flex items-center gap-2 mt-2">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add a caption…"
        autoFocus
        className="flex-1 bg-white/10 text-white placeholder:text-gray-400 text-sm px-3 py-1.5 rounded-lg border border-white/20 focus:outline-none"
      />
      <button onClick={() => onSave(value)} className="text-xs text-white bg-primary px-3 py-1.5 rounded-lg">Save</button>
      <button onClick={onCancel} className="text-xs text-gray-400">Cancel</button>
    </div>
  )
}

/**
 * Full-screen photo lightbox.
 *
 * Props:
 *   photos       — array of { id, file_path, file_name, caption, uploaded_by, bucket? }
 *   initialIndex — which photo to open first
 *   bucket       — Supabase Storage bucket name (default 'household-photos')
 *   userId       — current user id (for delete/caption permissions)
 *   isAdmin      — whether current user is household admin
 *   onClose      — called when lightbox closes
 *   onDelete     — (photoId) => Promise<void>
 *   onEditCaption — (photoId, caption) => Promise<void>
 */
export const LightboxViewer = ({
  photos,
  initialIndex = 0,
  bucket = 'household-photos',
  userId,
  isAdmin,
  onClose,
  onDelete,
  onEditCaption,
}) => {
  const [index,        setIndex]       = useState(initialIndex)
  const [urls,         setUrls]        = useState({})  // photoId → url
  const [editingCaption, setEditingCaption] = useState(false)
  const [deleting,     setDeleting]    = useState(false)
  const [confirmDel,   setConfirmDel]  = useState(false)
  const touchStartX    = useRef(null)

  const photo = photos[index]
  const url   = urls[photo?.id]

  // Preload current + adjacent photos
  useEffect(() => {
    const toLoad = [index - 1, index, index + 1].filter((i) => i >= 0 && i < photos.length)
    toLoad.forEach(async (i) => {
      const p = photos[i]
      if (!p || urls[p.id]) return
      if (!navigator.onLine) return
      try {
        const signed = await getSignedUrl(p.bucket ?? bucket, p.file_path)
        setUrls((prev) => ({ ...prev, [p.id]: signed }))
      } catch {}
    })
  }, [index, photos, bucket])

  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), [])
  const next = useCallback(() => setIndex((i) => Math.min(photos.length - 1, i + 1)), [photos.length])

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  prev()
      if (e.key === 'ArrowRight') next()
      if (e.key === 'Escape')     onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [prev, next, onClose])

  // Swipe navigation
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX }
  const handleTouchEnd   = (e) => {
    if (touchStartX.current === null) return
    const delta = e.changedTouches[0].clientX - touchStartX.current
    if (Math.abs(delta) > 50) delta < 0 ? next() : prev()
    touchStartX.current = null
  }

  const canEdit = isAdmin || photo?.uploaded_by === userId

  const handleDelete = async () => {
    if (!onDelete) return
    setDeleting(true)
    try {
      await onDelete(photo.id)
      if (photos.length <= 1) {
        onClose()
      } else {
        setIndex((i) => Math.min(i, photos.length - 2))
        setConfirmDel(false)
      }
    } finally {
      setDeleting(false)
    }
  }

  const handleSaveCaption = async (caption) => {
    if (onEditCaption) await onEditCaption(photo.id, caption)
    setEditingCaption(false)
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/60 shrink-0">
        <span className="text-white text-sm font-medium">{index + 1} of {photos.length}</span>
        <div className="flex items-center gap-2">
          {canEdit && onEditCaption && (
            <button
              onClick={() => setEditingCaption(true)}
              className="p-1.5 text-gray-300 hover:text-white"
              title="Edit caption"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
            </button>
          )}
          {/* Download */}
          {url && (
            <a
              href={url}
              download={photo?.file_name}
              className="p-1.5 text-gray-300 hover:text-white"
              title="Download"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
              </svg>
            </a>
          )}
          {canEdit && onDelete && !confirmDel && (
            <button
              onClick={() => setConfirmDel(true)}
              className="p-1.5 text-gray-300 hover:text-red-400"
              title="Delete photo"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
            </button>
          )}
          {confirmDel && (
            <>
              <button onClick={() => setConfirmDel(false)} className="text-xs text-gray-300 px-2 py-1">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="text-xs text-red-400 font-semibold px-2 py-1 disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </>
          )}
          <button onClick={onClose} className="p-1.5 text-gray-300 hover:text-white">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Photo area */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Prev arrow */}
        {index > 0 && (
          <button
            onClick={prev}
            className="absolute left-2 z-10 w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
        )}

        {!navigator.onLine && !url ? (
          <OfflinePlaceholder />
        ) : url ? (
          <img
            src={url}
            alt={photo?.file_name}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="w-12 h-12 rounded-full border-2 border-white border-t-transparent animate-spin" />
        )}

        {/* Next arrow */}
        {index < photos.length - 1 && (
          <button
            onClick={next}
            className="absolute right-2 z-10 w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        )}
      </div>

      {/* Caption area */}
      <div className="px-4 py-3 bg-black/60 min-h-[60px] shrink-0">
        {editingCaption ? (
          <CaptionEditor
            caption={photo?.caption}
            onSave={handleSaveCaption}
            onCancel={() => setEditingCaption(false)}
          />
        ) : (
          <p className="text-gray-300 text-sm text-center">
            {photo?.caption ?? <span className="opacity-40 italic">No caption</span>}
          </p>
        )}
      </div>
    </div>
  )
}
